import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Member } from '../../../core/services/member.service';
import { BusinessUnitStore } from '../../../core/stores/business-unit.store';
import { AuthService } from '../../../core/services/auth.service';
import { HasPermissionDirective } from '../../../core/directives/has-permission.directive';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { CustomFieldsInputComponent } from '../../../shared/components/custom-fields-input/custom-fields-input.component';

export interface DrawerBusyState {
  reset: boolean;
  invite: boolean;
  deactivate: boolean;
  createUser: boolean;
}

@Component({
  selector: 'app-member-quick-view-drawer',
  standalone: true,
  imports: [CommonModule, HasPermissionDirective, SpinnerComponent, CustomFieldsInputComponent],
  templateUrl: './member-quick-view-drawer.component.html'
})
export class MemberQuickViewDrawerComponent {
  private buStore = inject(BusinessUnitStore);
  readonly auth = inject(AuthService);

  @Input() set open(value: boolean) { this.openSig.set(value); }
  @Input() set member(value: Member | null) { this.memberSig.set(value); }
  @Input() selectedBuId: string | null = null;
  @Input() busy: DrawerBusyState = { reset: false, invite: false, deactivate: false, createUser: false };

  @Output() closed = new EventEmitter<void>();
  @Output() recordPayment = new EventEmitter<Member>();
  @Output() editMember = new EventEmitter<Member>();
  @Output() selectHousehold = new EventEmitter<Member>();
  @Output() createUserAccount = new EventEmitter<Member>();
  @Output() resetPassword = new EventEmitter<Member>();
  @Output() manageRoles = new EventEmitter<Member>();
  @Output() manageBusinessUnits = new EventEmitter<Member>();
  @Output() invite = new EventEmitter<Member>();
  @Output() deactivateUser = new EventEmitter<Member>();
  @Output() openFullProfile = new EventEmitter<Member>();

  readonly openSig = signal(false);
  readonly memberSig = signal<Member | null>(null);

  readonly displayMember = computed(() => this.memberSig());

  buName(buId: string): string {
    return this.buStore.byId().get(buId)?.name ?? `BU ${buId}`;
  }

  isCurrentBu(buId: string): boolean {
    return this.selectedBuId === buId;
  }

  getInitials(name: string): string {
    const parts = (name || '').trim().split(' ').filter(p => p);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name || '?').substring(0, 2).toUpperCase();
  }

  statusBadgeClass(status: string | undefined): string {
    switch (status) {
      case 'Active':   return 'bg-green-100 text-green-700';
      case 'Inactive': return 'bg-amber-100 text-amber-700';
      case 'Left':     return 'bg-gray-100 text-gray-600';
      default:         return 'bg-gray-100 text-gray-600';
    }
  }

  accountStatusLabel(m: Member): string {
    if (!m.userAccount) return 'No account';
    return m.userAccount.status === 'Active' ? 'Active' : 'Inactive';
  }

  accountPillClass(m: Member): string {
    if (!m.userAccount) return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
    return m.userAccount.status === 'Active'
      ? 'bg-green-50 text-green-700 ring-1 ring-green-200'
      : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200';
  }

  feeShortfall(m: Member): number {
    const expected = m.currentPeriodExpected ?? m.monthlyFee ?? 0;
    const paid = m.currentPeriodTotalPaid ?? 0;
    return Math.max(expected - paid, 0);
  }

  close(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.openSig()) this.close();
  }

  emitRecordPayment(): void {
    const m = this.memberSig();
    if (m) this.recordPayment.emit(m);
  }

  emitEdit(): void {
    const m = this.memberSig();
    if (m) this.editMember.emit(m);
  }

  emitSelectHousehold(): void {
    const m = this.memberSig();
    if (m) this.selectHousehold.emit(m);
  }

  emitCreateUserAccount(): void {
    const m = this.memberSig();
    if (m) this.createUserAccount.emit(m);
  }

  emitResetPassword(): void {
    const m = this.memberSig();
    if (m) this.resetPassword.emit(m);
  }

  emitManageRoles(): void {
    const m = this.memberSig();
    if (m) this.manageRoles.emit(m);
  }

  emitManageBusinessUnits(): void {
    const m = this.memberSig();
    if (m) this.manageBusinessUnits.emit(m);
  }

  emitInvite(): void {
    const m = this.memberSig();
    if (m) this.invite.emit(m);
  }

  emitDeactivate(): void {
    const m = this.memberSig();
    if (m) this.deactivateUser.emit(m);
  }

  emitOpenFullProfile(): void {
    const m = this.memberSig();
    if (m) this.openFullProfile.emit(m);
  }
}
